import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Button, Stack,
} from "@mui/material";
import RedeemIcon from "@mui/icons-material/Redeem";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { SECTION_COLORS } from "../../constants";

const ViewBonusDialog = ({ open, bonus, onClose, onEditRequest }) => {
    if (!bonus) return null;

    const rows = [
        { label: "Clé bonus", value: bonus.bonus_key },
        { label: "Politique d'attribution", value: bonus.award_policy },
        { label: "Marque", value: bonus.brand || "Globale" },
        { label: "Identifiant", value: bonus.id },
        { label: "Créé le", value: bonus.created_at ? new Date(bonus.created_at).toLocaleDateString("fr-FR") : "–" },
        { label: "Mis à jour le", value: bonus.updated_at ? new Date(bonus.updated_at).toLocaleDateString("fr-FR") : "–" },
    ];

    const hasParams = bonus.policy_params && Object.keys(bonus.policy_params).length > 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.bonus.gradient }}>
                        <RedeemIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            {bonus.name}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">Détail de la définition de bonus</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {/* Status banner */}
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={2} py={1.25}
                    sx={{
                        borderRadius: 2,
                        bgcolor: bonus.active ? "#f0fdf4" : "#fef2f2",
                        border: `1px solid ${bonus.active ? "#86efac" : "#fca5a5"}`
                    }}>
                    {bonus.active
                        ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                        : <CancelIcon sx={{ fontSize: 18, color: "#DC2626" }} />}
                    <Typography fontSize={13} fontWeight={600} color={bonus.active ? "#16a34a" : "#DC2626"}>
                        {bonus.active ? "Actif" : "Inactif"}
                    </Typography>
                    <Chip label={bonus.award_policy} size="small"
                        sx={{
                            ml: "auto", fontWeight: 700, fontSize: 11,
                            bgcolor: SECTION_COLORS.bonus.light, color: SECTION_COLORS.bonus.main
                        }} />
                </Box>

                {/* Description */}
                {bonus.description && (
                    <Typography fontSize={13} color="text.secondary" mb={2}
                        sx={{ fontStyle: "italic", borderLeft: `3px solid ${SECTION_COLORS.bonus.main}50`, pl: 1.5 }}>
                        {bonus.description}
                    </Typography>
                )}

                {/* Detail rows */}
                <Stack spacing={1}>
                    {rows.map(({ label, value }) => (
                        <Box key={label} display="flex" alignItems="flex-start" justifyContent="space-between"
                            px={2} py={1} sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={12} color="text.secondary" fontWeight={500} sx={{ minWidth: 160 }}>{label}</Typography>
                            <Typography fontSize={12} fontWeight={600} color="#1a1a2e" sx={{ textAlign: "right", wordBreak: "break-all" }}>{value}</Typography>
                        </Box>
                    ))}
                </Stack>

                {/* policy_params */}
                {hasParams && (
                    <Box mt={2} p={1.5} sx={{ borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                        <Typography fontSize={11} fontWeight={700} color={SECTION_COLORS.bonus.main} mb={0.5}>
                            policy_params
                        </Typography>
                        <Typography
                            component="pre"
                            fontSize={11}
                            sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#1a1a2e", m: 0 }}>
                            {JSON.stringify(bonus.policy_params, null, 2)}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
                <Button variant="contained" onClick={() => { onClose(); onEditRequest(bonus); }}
                    startIcon={<EditIcon />}
                    sx={{
                        background: SECTION_COLORS.bonus.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.bonus.main}40` }
                    }}>
                    Modifier
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewBonusDialog;
